<script setup lang="ts">
import { computed, inject, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';

import LocalizedInput from '@/components/LocalizedInput.vue';
import LocalizedEditor from '@/components/base/LocalizedEditor.vue';
import StandardToggle from '@/components/StandardToggle.vue';
import MapDefinition from '@/components/map/MapDefinition.vue';

import type { Locales, ProjectModuleDto, ProjectModuleMapConfigDto } from '@/services/types/api.types';
import type { MapSize } from '@/components/map/mapLayer.types';
import { useConfigurationStore } from '@/stores/configurations.store';
import { useBlocksStore } from '@/stores';
import { Constants } from '@/constants';

// CONST
const { t } = useI18n();
const isProjectTOCEnabled = useConfigurationStore().isFeatureEnabled('projectTOC');
/**
 * The size the project page draws the map at on a desktop, so the admin frames the area participants
 * are shown. The web map is 16:9, the shape of a Video module (web `Map.vue`, `.video-aspect-ratio`),
 * and as wide as the module column: 1280px in the modern theme (`CkContainer`, `max-w-7xl`), 70% of
 * the window in the default theme. Its shape is the same at any width, and the admin canvas is always
 * smaller than this, so it only ever takes the shape.
 */
const WEB_MAP_SIZE: MapSize = { width: 1280, height: 720 };

// STORE
const blocksStore = useBlocksStore();

// PROPS
const props = defineProps<{
  languageEnabled: Locales[];
  editLocale: Locales;
  module: ProjectModuleDto;
}>();

// COMPUTED
/**
 * `ModuleDetailView` hands every module component its props through `v-bind` on a plain object, so
 * `editLocale` arrives as the ref itself rather than its value. That is harmless for the localized
 * inputs, which read the `editLocale` injection and ignore the prop, but `MapDefinition` indexes
 * layer names with it in script — so resolve it the same way the inputs do, and keep the prop only
 * as a fallback for a caller that passes a plain locale.
 */
const injectedEditLocale = inject<Ref<Locales>>('editLocale');
const editLocale = computed<Locales>(() => injectedEditLocale?.value ?? props.editLocale ?? Constants.DefaultLocale);

const mapConfig = computed(() => props.module.config as ProjectModuleMapConfigDto);

// FUNCTIONS
async function uploadLayer(file: File): Promise<boolean> {
  return await blocksStore.uploadMapLayer(props.module, { file, version: 'v1' });
}

async function deleteLayer(layerId: number): Promise<boolean> {
  return await blocksStore.deleteMapLayer(props.module, layerId);
}
</script>
<template>
  <div class="module-map">
    <localized-input
      name="title"
      :label="t('global.label.title')"
      :editLocale="editLocale"
      outlined
      :language_enabled="props.languageEnabled"
    />
    <q-field :label="t('global.label.description')" stack-label borderless>
      <template v-slot:control>
        <localized-editor
          name="config.description"
          :label="t('global.label.description')"
          class="q-mt-sm full-width"
          :editLocale="editLocale"
          :language_enabled="props.languageEnabled"
        />
      </template>
    </q-field>
    <!--
      Keyed on the module so that switching records from the breadcrumb rebuilds the editor: the
      boundary map reads the saved bounds, zoom and style once, when it is created, and the tabs
      are kept alive, so a reused instance would frame the previous module's area.
    -->
    <map-definition
      :key="props.module.id"
      field-prefix="config"
      :config="mapConfig"
      :edit-locale="editLocale"
      :upload-layer="uploadLayer"
      :delete-layer="deleteLayer"
      :participant-map-size="WEB_MAP_SIZE"
    />
    <standard-toggle name="config.hideAddressSearch" :label="t('project-module.map.hideAddressSearch')" outlined />
    <standard-toggle name="config.highlight" :label="t('project-module.admin.highlight')" outlined />
    <standard-toggle
      v-if="isProjectTOCEnabled"
      name="config.includeInTableOfContents"
      :label="t('project-module.admin.includeInTableOfContents')"
      outlined
    />
  </div>
</template>
